// HTTP adapter — talks to the FastAPI backend at REACT_APP_BACKEND_URL.
//
// All functions return promises. Shapes match what the mock adapter used
// to return, so components stay unchanged after the migration.
//
// The backend response is already shaped as the UI expects (camelCase
// meta, id/title/summary/…) — no client-side transformation needed.

const RAW_BASE = process.env.REACT_APP_BACKEND_URL;
if (!RAW_BASE && typeof window !== "undefined") {
  // eslint-disable-next-line no-console
  console.warn("REACT_APP_BACKEND_URL is not defined — will use relative /api paths.");
}
// If the frontend origin matches the backend URL (same-origin deployment),
// use RELATIVE paths so ingress + browser cookies behave predictably.
const useRelative =
  typeof window !== "undefined" &&
  (!RAW_BASE || window.location.origin === RAW_BASE.replace(/\/$/, ""));
const API = useRelative ? "/api" : `${(RAW_BASE || "").replace(/\/$/, "")}/api`;

const request = async (path, opts = {}) => {
  const { signal, method = "GET", body, headers = {} } = opts;
  const url = `${API}${path}`;
  const init = {
    method,
    // Include cookies so /api/auth/* + /api/admin/* endpoints receive
    // the httpOnly session cookies set by /api/auth/login.
    credentials: "include",
    headers: { Accept: "application/json", ...headers },
    signal,
  };
  if (body !== undefined) {
    init.headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  const res = await fetch(url, init);
  if (res.status === 204) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`${method} ${path} failed: ${res.status} ${text}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
};

export { request };

/* ─── Opportunities ────────────────────────────────────────────────── */

export const httpListOpportunities = (opts) => request("/opportunities", opts);

export const httpGetFeaturedOpportunity = async (opts) => {
  try {
    return await request("/opportunities/featured", opts);
  } catch (err) {
    if (String(err).includes(" 404 ")) return null;
    throw err;
  }
};

export const httpGetOpportunityById = async (id, opts) => {
  try {
    return await request(`/opportunities/${encodeURIComponent(id)}`, opts);
  } catch (err) {
    if (String(err).includes(" 404 ")) return null;
    throw err;
  }
};

/* ─── Reference data ───────────────────────────────────────────────── */

export const httpListCountries        = (opts) => request("/countries", opts);
export const httpListIndustries       = (opts) => request("/industries", opts);
export const httpListOpportunityTypes = (opts) => request("/opportunity-types", opts);

/* ─── Bootstrap ────────────────────────────────────────────────────── */

export const httpBootstrap = (opts) => request("/bootstrap", opts);

/* ─── Public stats (Sprint 28a — replaces hardcoded hero KPIs) ────── */

export const httpGetPublicStats = (opts) => request("/stats/public", opts);
